import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useAddress } from '@/context/AddressContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lock, MapPin, Plus, Check, ChevronDown, Home, Briefcase, MapPinned, 
  User, UserCheck, ArrowLeft, Truck, Smartphone, MessageCircle, Upload,
  QrCode, Copy, CheckCircle, Clock, ArrowRight
} from 'lucide-react';
import AddressModal from '@/components/address/AddressModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WhatsApp business number
const WHATSAPP_NUMBER = '971503847865';

// UPI Details
const UPI_ID = 'shaikhtufayl17@okicici';
const UPI_QR_IMAGE = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=upi://pay?pa=shaikhtufayl17@okicici&pn=DXB2BOM&cu=INR';

// Country codes for phone dropdown
const COUNTRY_CODES = [
  { code: '+91', country: 'IN', name: 'India' },
  { code: '+971', country: 'AE', name: 'UAE' },
  { code: '+1', country: 'US', name: 'USA' },
  { code: '+44', country: 'GB', name: 'UK' },
  { code: '+966', country: 'SA', name: 'Saudi Arabia' },
  { code: '+968', country: 'OM', name: 'Oman' },
  { code: '+974', country: 'QA', name: 'Qatar' },
  { code: '+965', country: 'KW', name: 'Kuwait' },
  { code: '+973', country: 'BH', name: 'Bahrain' },
  { code: '+65', country: 'SG', name: 'Singapore' },
];

// Indian states for dropdown
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
];

const labelIcons = {
  Home: Home,
  Work: Briefcase,
  Other: MapPinned
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, cartSubtotal, shippingFee, cartTotal, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { addresses, selectedAddress, selectAddress, fetchAddresses } = useAddress();
  
  // Checkout step: 1 = Address, 2 = Payment, 3 = Confirmation
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState(isAuthenticated ? 'saved' : 'guest');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  
  // Payment method: 'pod' (Cash on Delivery), 'upi', 'whatsapp'
  const [paymentMethod, setPaymentMethod] = useState('pod');
  const [paymentProof, setPaymentProof] = useState(null);

  // Promo code
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // {code, discount, description}
  const [promoLoading, setPromoLoading] = useState(false);
  const promoDiscount = appliedPromo?.discount || 0;
  const payableTotal = Math.max(0, cartTotal - promoDiscount);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promocodes/validate`, { code: promoInput.trim(), subtotal: cartSubtotal });
      setAppliedPromo(res.data);
      toast.success(`Code ${res.data.code} applied — you save ₹${res.data.discount.toLocaleString()}!`);
    } catch (error) {
      setAppliedPromo(null);
      toast.error(error?.response?.data?.detail || 'Invalid promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
  };
  const [paymentProofPreview, setPaymentProofPreview] = useState(null);
  const [upiPaid, setUpiPaid] = useState(false);
  const [copiedUPI, setCopiedUPI] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(null);
  
  const fileInputRef = useRef(null);
  const countryDropdownRef = useRef(null);

  // Structured address form
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    country_code: '+91',
    customer_phone: '',
    flat_building: '',
    street_road: '',
    area_locality: '',
    city: '',
    state: '',
    pincode: ''
  });

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-fill from selected address and user data
  useEffect(() => {
    if (selectedAddress) {
      setFormData(prev => ({
        ...prev,
        customer_name: selectedAddress.full_name || prev.customer_name,
        customer_phone: selectedAddress.phone || prev.customer_phone,
        flat_building: selectedAddress.address_line_1 || '',
        street_road: selectedAddress.address_line_2 || '',
        area_locality: selectedAddress.landmark || '',
        city: selectedAddress.city || '',
        state: selectedAddress.state || '',
        pincode: selectedAddress.pincode || ''
      }));
    }
  }, [selectedAddress]);

  // Auto-fill email from user
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({
        ...prev,
        customer_email: user.email,
        customer_name: prev.customer_name || user.name || ''
      }));
    }
  }, [user]);

  // Build full delivery address string from structured fields
  const getFullDeliveryAddress = () => {
    const parts = [
      formData.flat_building,
      formData.street_road,
      formData.area_locality,
      formData.city,
      formData.state,
      formData.pincode ? `PIN: ${formData.pincode}` : ''
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Build full phone number with country code
  const getFullPhoneNumber = () => {
    return formData.customer_phone ? `${formData.country_code}${formData.customer_phone}` : '';
  };

  if (cart.length === 0 && !orderPlaced) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-slate-500 mb-4">Your cart is empty</p>
          <Button onClick={() => navigate('/products')}>Browse Products</Button>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectAddress = (address) => {
    selectAddress(address);
    setShowAddressDropdown(false);
  };

  const validateAddress = () => {
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast.error('Please fill your name, email and phone number');
      return false;
    }
    if (!formData.flat_building || !formData.city || !formData.state || !formData.pincode) {
      toast.error('Please fill all address fields');
      return false;
    }
    if (formData.pincode.length !== 6) {
      toast.error('Please enter a valid 6-digit PIN code');
      return false;
    }
    return true;
  };

  const handleContinueToPayment = () => {
    if (validateAddress()) {
      setStep(2);
    }
  };

  const copyUPIId = () => {
    navigator.clipboard.writeText(UPI_ID);
    setCopiedUPI(true);
    toast.success('UPI ID copied!');
    setTimeout(() => setCopiedUPI(false), 2000);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      setPaymentProof(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaymentProofPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateOrderSummaryText = () => {
    let text = `*DXB2BOM Order*\n\n`;
    text += `*Customer:* ${formData.customer_name}\n`;
    text += `*Phone:* ${getFullPhoneNumber()}\n`;
    text += `*Email:* ${formData.customer_email}\n\n`;
    text += `*Delivery Address:*\n${getFullDeliveryAddress()}\n\n`;
    text += `*Order Items:*\n`;
    cart.forEach((item, idx) => {
      text += `${idx + 1}. ${item.name} x ${item.quantity} = ₹${(item.price * item.quantity).toLocaleString()}\n`;
    });
    text += `\n*Subtotal:* ₹${cartSubtotal.toLocaleString()}\n`;
    if (appliedPromo) {
      text += `*Promo (${appliedPromo.code}):* -₹${promoDiscount.toLocaleString()}\n`;
    }
    text += `*Shipping:* ${shippingFee === 0 ? 'Free' : `₹${shippingFee.toLocaleString()}`}\n`;
    text += `*Total:* ₹${payableTotal.toLocaleString()}\n\n`;
    text += `*Payment Method:* ${paymentMethod === 'pod' ? 'Cash on Delivery' : paymentMethod === 'upi' ? 'UPI Payment' : 'WhatsApp Checkout'}`;
    return text;
  };

  const openWhatsAppCheckout = () => {
    const text = encodeURIComponent(generateOrderSummaryText());
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank');
  };

  const placeOrder = async () => {
    setLoading(true);
    try {
      // Determine order status based on payment method
      let orderStatus = 'pending';
      let paymentStatus = 'pending';
      
      if (paymentMethod === 'pod') {
        orderStatus = 'confirmed';
        paymentStatus = 'pay_on_delivery';
      } else if (paymentMethod === 'upi') {
        orderStatus = 'awaiting_verification';
        paymentStatus = 'awaiting_verification';
      } else if (paymentMethod === 'whatsapp') {
        orderStatus = 'pending';
        paymentStatus = 'pending';
      }

      const orderData = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: getFullPhoneNumber(),
        delivery_address: getFullDeliveryAddress(),
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        total_amount: payableTotal,
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        status: orderStatus,
        promo_code: appliedPromo?.code || null
      };

      const response = await axios.post(`${API}/orders`, orderData);
      const order = response.data;

      // If UPI payment with proof, we could upload it here (future enhancement)
      
      setOrderPlaced(order);
      setStep(3);
      clearCart();
      
      if (paymentMethod === 'whatsapp') {
        openWhatsAppCheckout();
      }
      
      toast.success('Order placed successfully!');
    } catch (error) {
      console.error('Order error:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = () => {
    if (paymentMethod === 'upi' && !upiPaid) {
      toast.error('Please confirm that you have made the payment');
      return;
    }
    placeOrder();
  };

  // Step indicators
  const steps = [
    { num: 1, label: 'Address' },
    { num: 2, label: 'Payment' },
    { num: 3, label: 'Confirmation' }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-10">
        {/* Back Button */}
        {step < 3 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => step === 1 ? navigate(-1) : setStep(step - 1)}
            className="mb-4 text-slate-600 hover:text-slate-900 -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {step === 1 ? 'Back' : 'Back to Address'}
          </Button>
        )}

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((s, idx) => (
            <div key={s.num} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                step >= s.num 
                  ? 'bg-[#0047AB] text-white' 
                  : 'bg-slate-200 text-slate-500'
              }`}>
                {step > s.num ? <Check className="w-4 h-4" /> : s.num}
              </div>
              <span className={`ml-2 text-sm font-medium ${
                step >= s.num ? 'text-[#0047AB]' : 'text-slate-400'
              }`}>
                {s.label}
              </span>
              {idx < steps.length - 1 && (
                <div className={`w-12 md:w-20 h-0.5 mx-3 ${
                  step > s.num ? 'bg-[#0047AB]' : 'bg-slate-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Left Column - Forms */}
          <motion.div
            key={step}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Step 1: Address */}
            {step === 1 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-outfit font-semibold mb-6 flex items-center gap-2">
                  <MapPin className="w-6 h-6 text-[#0047AB]" />
                  Delivery Details
                </h2>
                
                {/* Checkout Mode Toggle */}
                {isAuthenticated && addresses.length > 0 && (
                  <div className="mb-6">
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Checkout as</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setCheckoutMode('saved')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          checkoutMode === 'saved'
                            ? 'border-[#0047AB] bg-[#0047AB]/5 text-[#0047AB]'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <UserCheck className="w-4 h-4" />
                        <span className="text-sm font-medium">Saved Address</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCheckoutMode('guest')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          checkoutMode === 'guest'
                            ? 'border-[#0047AB] bg-[#0047AB]/5 text-[#0047AB]'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <User className="w-4 h-4" />
                        <span className="text-sm font-medium">Enter Manually</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Saved Addresses Dropdown */}
                {isAuthenticated && checkoutMode === 'saved' && addresses.length > 0 && (
                  <div className="mb-6">
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Select Saved Address</Label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowAddressDropdown(!showAddressDropdown)}
                        className="w-full flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg hover:border-[#0047AB] transition-colors text-left"
                        data-testid="address-selector"
                      >
                        {selectedAddress ? (
                          <div className="flex items-center gap-3">
                            {(() => {
                              const Icon = labelIcons[selectedAddress.type] || MapPinned;
                              return <Icon className="w-5 h-5 text-[#0047AB]" />;
                            })()}
                            <div>
                              <p className="font-medium text-slate-900">{selectedAddress.type || 'Home'} - {selectedAddress.full_name}</p>
                              <p className="text-sm text-slate-500 line-clamp-1">
                                {selectedAddress.address_line_1}, {selectedAddress.city}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-500">Select a delivery address</span>
                        )}
                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showAddressDropdown ? 'rotate-180' : ''}`} />
                      </button>

                      {showAddressDropdown && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowAddressDropdown(false)} />
                          <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            {addresses.map((address) => {
                              const Icon = labelIcons[address.type] || MapPinned;
                              const isSelected = selectedAddress?.id === address.id;
                              return (
                                <button
                                  key={address.id}
                                  type="button"
                                  onClick={() => handleSelectAddress(address)}
                                  className={`w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${isSelected ? 'bg-[#0047AB]/5' : ''}`}
                                >
                                  <Icon className={`w-5 h-5 ${isSelected ? 'text-[#0047AB]' : 'text-slate-400'}`} />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-slate-900">{address.type || 'Home'}</span>
                                      {address.is_default && (
                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Default</span>
                                      )}
                                    </div>
                                    <p className="text-sm text-slate-600">{address.full_name}</p>
                                    <p className="text-sm text-slate-500 line-clamp-1">{address.address_line_1}, {address.city} - {address.pincode}</p>
                                  </div>
                                  {isSelected && <Check className="w-5 h-5 text-[#0047AB]" />}
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => { setShowAddressDropdown(false); setShowAddressModal(true); }}
                              className="w-full flex items-center gap-3 p-4 text-[#0047AB] hover:bg-[#0047AB]/5 font-medium"
                            >
                              <Plus className="w-5 h-5" />
                              Add New Address
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Manual Form */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer_name">Full Name *</Label>
                      <Input id="customer_name" name="customer_name" value={formData.customer_name} onChange={handleChange} required data-testid="customer-name-input" placeholder="John Doe" />
                    </div>
                    <div>
                      <Label htmlFor="customer_email">Email *</Label>
                      <Input id="customer_email" name="customer_email" type="email" value={formData.customer_email} onChange={handleChange} required data-testid="customer-email-input" placeholder="you@example.com" />
                    </div>
                  </div>

                  {/* Phone with country code */}
                  <div>
                    <Label htmlFor="customer_phone">Phone Number *</Label>
                    <div className="flex gap-2">
                      <div className="relative" ref={countryDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                          className="h-10 flex items-center gap-1 px-3 border border-slate-200 rounded-md bg-white hover:border-slate-300 transition-colors text-sm font-medium text-slate-900 whitespace-nowrap"
                          data-testid="country-code-btn"
                        >
                          {formData.country_code}
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        {showCountryDropdown && (
                          <div className="absolute z-30 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {COUNTRY_CODES.map((c) => (
                              <button
                                key={c.code}
                                type="button"
                                onClick={() => {
                                  setFormData({ ...formData, country_code: c.code });
                                  setShowCountryDropdown(false);
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 ${formData.country_code === c.code ? 'bg-[#0047AB]/5 text-[#0047AB]' : 'text-slate-700'}`}
                                data-testid={`country-code-option-${c.country}`}
                              >
                                <span>{c.name}</span>
                                <span className="font-mono text-slate-500">{c.code}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Input
                        id="customer_phone"
                        name="customer_phone"
                        type="tel"
                        value={formData.customer_phone}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 15);
                          setFormData({ ...formData, customer_phone: digits });
                        }}
                        required
                        data-testid="customer-phone-input"
                        placeholder="9876543210"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  {/* Structured Address Fields */}
                  <div className="pt-2">
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">Delivery Address</Label>
                    <div className="space-y-3">
                      <Input
                        name="flat_building"
                        value={formData.flat_building}
                        onChange={handleChange}
                        required
                        data-testid="flat-building-input"
                        placeholder="Flat / House No., Building, Apartment *"
                      />
                      <Input
                        name="street_road"
                        value={formData.street_road}
                        onChange={handleChange}
                        data-testid="street-road-input"
                        placeholder="Street / Road / Landmark"
                      />
                      <Input
                        name="area_locality"
                        value={formData.area_locality}
                        onChange={handleChange}
                        data-testid="area-locality-input"
                        placeholder="Area / Locality / Sector"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          data-testid="city-input"
                          placeholder="City / Town / District *"
                        />
                        <select
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          required
                          data-testid="state-select"
                          className="h-10 px-3 border border-slate-200 rounded-md bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0047AB]/30 focus:border-[#0047AB]"
                        >
                          <option value="">Select State *</option>
                          {INDIAN_STATES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <Input
                        name="pincode"
                        value={formData.pincode}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setFormData({ ...formData, pincode: digits });
                        }}
                        required
                        data-testid="pincode-input"
                        placeholder="6-digit PIN Code *"
                        maxLength={6}
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={handleContinueToPayment}
                    className="w-full bg-[#0047AB] hover:bg-[#003380] text-white shadow-lg h-12 text-base"
                    data-testid="continue-to-payment-btn"
                  >
                    Continue to Payment
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Payment Method */}
            {step === 2 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-outfit font-semibold mb-6 flex items-center gap-2">
                  <Smartphone className="w-6 h-6 text-[#0047AB]" />
                  Payment Method
                </h2>

                <div className="space-y-4">
                  {/* Cash on Delivery */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('pod')}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                      paymentMethod === 'pod' 
                        ? 'border-[#0047AB] bg-[#0047AB]/5' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="payment-pod"
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      paymentMethod === 'pod' ? 'bg-[#0047AB] text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      <Truck className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Cash on Delivery</p>
                      <p className="text-sm text-slate-500">Pay when your order arrives</p>
                    </div>
                    {paymentMethod === 'pod' && <Check className="w-6 h-6 text-[#0047AB]" />}
                  </button>

                  {/* UPI Payment */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('upi')}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                      paymentMethod === 'upi' 
                        ? 'border-[#0047AB] bg-[#0047AB]/5' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="payment-upi"
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      paymentMethod === 'upi' ? 'bg-[#0047AB] text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      <QrCode className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Pay with GPay / UPI</p>
                      <p className="text-sm text-slate-500">Scan QR or use UPI ID</p>
                    </div>
                    {paymentMethod === 'upi' && <Check className="w-6 h-6 text-[#0047AB]" />}
                  </button>

                  {/* UPI Payment Details */}
                  <AnimatePresence>
                    {paymentMethod === 'upi' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-slate-50 rounded-xl p-6 space-y-4"
                      >
                        <div className="flex flex-col items-center">
                          <p className="text-sm font-medium text-slate-700 mb-3">Scan QR Code to Pay</p>
                          <img 
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=upi://pay?pa=${UPI_ID}&pn=DXB2BOM&am=${payableTotal}&cu=INR`}
                            alt="UPI QR Code"
                            className="w-44 h-44 rounded-lg border border-slate-200 bg-white p-2"
                          />
                        </div>
                        
                        <div className="text-center">
                          <p className="text-sm text-slate-500 mb-1">Or pay using UPI ID</p>
                          <div className="flex items-center justify-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-2">
                            <span className="font-mono font-semibold text-slate-900">{UPI_ID}</span>
                            <button 
                              onClick={copyUPIId}
                              className="p-1 hover:bg-slate-100 rounded"
                            >
                              {copiedUPI ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-400" />}
                            </button>
                          </div>
                        </div>

                        <div className="text-center bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-sm text-amber-800">
                            <strong>Amount to Pay:</strong> ₹{payableTotal.toLocaleString()}
                          </p>
                        </div>

                        {/* Upload Payment Proof */}
                        <div>
                          <p className="text-sm font-medium text-slate-700 mb-2">Upload Payment Screenshot (Optional)</p>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept="image/*"
                            className="hidden"
                          />
                          {paymentProofPreview ? (
                            <div className="relative">
                              <img src={paymentProofPreview} alt="Payment proof" className="w-full h-32 object-contain rounded-lg border border-slate-200 bg-white" />
                              <button
                                onClick={() => { setPaymentProof(null); setPaymentProofPreview(null); }}
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 text-xs"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-[#0047AB] hover:bg-[#0047AB]/5 transition-colors"
                            >
                              <Upload className="w-5 h-5 text-slate-400" />
                              <span className="text-sm text-slate-600">Click to upload screenshot</span>
                            </button>
                          )}
                        </div>

                        {/* I Have Paid Checkbox */}
                        <label className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg cursor-pointer hover:border-[#0047AB]">
                          <input
                            type="checkbox"
                            checked={upiPaid}
                            onChange={(e) => setUpiPaid(e.target.checked)}
                            className="w-5 h-5 text-[#0047AB] rounded border-slate-300 focus:ring-[#0047AB]"
                          />
                          <span className="font-medium text-slate-900">I have completed the payment</span>
                        </label>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* WhatsApp Checkout */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('whatsapp')}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                      paymentMethod === 'whatsapp' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="payment-whatsapp"
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      paymentMethod === 'whatsapp' ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      <MessageCircle className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">WhatsApp Checkout</p>
                      <p className="text-sm text-slate-500">Complete order via WhatsApp chat</p>
                    </div>
                    {paymentMethod === 'whatsapp' && <Check className="w-6 h-6 text-green-500" />}
                  </button>

                  {/* Place Order Button */}
                  <Button
                    onClick={handlePlaceOrder}
                    disabled={loading || (paymentMethod === 'upi' && !upiPaid)}
                    className={`w-full h-12 text-base font-semibold shadow-lg ${
                      paymentMethod === 'whatsapp' 
                        ? 'bg-green-500 hover:bg-green-600' 
                        : 'bg-[#0047AB] hover:bg-[#003380]'
                    }`}
                    data-testid="place-order-btn"
                  >
                    {loading ? (
                      'Processing...'
                    ) : paymentMethod === 'whatsapp' ? (
                      <>
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Place Order via WhatsApp
                      </>
                    ) : (
                      <>
                        <Lock className="w-5 h-5 mr-2" />
                        Place Order
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    By placing this order, you agree to our Terms & Conditions
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && orderPlaced && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                
                <h2 className="text-2xl font-outfit font-bold text-slate-900 mb-2">
                  Order Placed Successfully!
                </h2>
                
                <p className="text-slate-600 mb-4">
                  Order ID: <span className="font-mono font-semibold">{orderPlaced.id}</span>
                </p>

                {paymentMethod === 'upi' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-center gap-2 text-amber-800">
                      <Clock className="w-5 h-5" />
                      <span className="font-medium">Awaiting Payment Verification</span>
                    </div>
                    <p className="text-sm text-amber-700 mt-1">
                      Our team will verify your payment and update the order status.
                    </p>
                  </div>
                )}

                {paymentMethod === 'pod' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-center gap-2 text-blue-800">
                      <Truck className="w-5 h-5" />
                      <span className="font-medium">Cash on Delivery</span>
                    </div>
                    <p className="text-sm text-blue-700 mt-1">
                      Pay ₹{orderPlaced.total_amount.toLocaleString()} when your order arrives.
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/orders')}
                    className="w-full bg-[#0047AB] hover:bg-[#003380]"
                  >
                    View My Orders
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => navigate('/products')}
                    className="w-full"
                  >
                    Continue Shopping
                  </Button>

                  {/* WhatsApp Support */}
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi, I just placed order #${orderPlaced.id}. Can you confirm?`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full p-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Contact Us on WhatsApp
                  </a>
                </div>
              </div>
            )}
          </motion.div>

          {/* Right Column - Order Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8 sticky top-24">
              <h2 className="text-xl md:text-2xl font-outfit font-semibold mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {(step === 3 && orderPlaced ? orderPlaced.items : cart).map((item, idx) => (
                  <div key={item.id || idx} className="flex gap-4">
                    <img
                      src={item.image_url || 'https://via.placeholder.com/64'}
                      alt={item.name || item.product_name}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <p className="font-dm-sans font-semibold text-sm">{item.name || item.product_name}</p>
                      <p className="text-sm text-slate-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-dm-sans font-semibold">
                      ₹{((item.price) * item.quantity).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-2">
                {/* Promo code input */}
                {step < 3 && (
                  <div className="pb-3">
                    {appliedPromo ? (
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2" data-testid="applied-promo">
                        <div>
                          <p className="text-sm font-semibold text-green-700 font-mono">{appliedPromo.code}</p>
                          <p className="text-xs text-green-600">You save ₹{promoDiscount.toLocaleString()}</p>
                        </div>
                        <button onClick={removePromo} className="text-xs font-medium text-red-500 hover:text-red-700" data-testid="remove-promo-btn">
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                          onKeyDown={(e) => e.key === 'Enter' && applyPromo()}
                          placeholder="Promo code"
                          className="flex-1 h-9 px-3 border border-slate-200 rounded-md text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-[#0047AB]/30"
                          data-testid="promo-code-input"
                        />
                        <button
                          onClick={applyPromo}
                          disabled={promoLoading || !promoInput.trim()}
                          className="px-4 h-9 rounded-md text-sm font-medium bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40 transition-colors"
                          data-testid="apply-promo-btn"
                        >
                          {promoLoading ? '…' : 'Apply'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span>
                  <span>₹{(step === 3 && orderPlaced ? orderPlaced.total_amount + (orderPlaced.discount_amount || 0) - shippingFee : cartSubtotal).toLocaleString()}</span>
                </div>
                {(step === 3 && orderPlaced ? orderPlaced.discount_amount > 0 : promoDiscount > 0) && (
                  <div className="flex justify-between text-green-600 font-medium" data-testid="discount-row">
                    <span>Discount{(step === 3 && orderPlaced ? orderPlaced.promo_code : appliedPromo?.code) ? ` (${step === 3 && orderPlaced ? orderPlaced.promo_code : appliedPromo.code})` : ''}</span>
                    <span>−₹{(step === 3 && orderPlaced ? orderPlaced.discount_amount : promoDiscount).toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-600">
                  <span>Shipping</span>
                  <span className={shippingFee === 0 ? 'text-green-600 font-medium' : ''}>
                    {shippingFee === 0 ? 'Free' : `₹${shippingFee.toLocaleString()}`}
                  </span>
                </div>
                <div className="flex justify-between text-xl font-outfit font-bold text-slate-900 pt-4 border-t">
                  <span>Total</span>
                  <span className="text-[#0047AB]" data-testid="checkout-total">
                    ₹{(step === 3 && orderPlaced ? orderPlaced.total_amount : payableTotal).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* WhatsApp Help */}
              {step < 3 && (
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi, I need help with my order on DXB2BOM')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 flex items-center justify-center gap-2 w-full p-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg font-medium transition-colors border border-green-200"
                >
                  <MessageCircle className="w-5 h-5" />
                  Need Help? Chat on WhatsApp
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Address Modal */}
      <AddressModal 
        isOpen={showAddressModal} 
        onClose={() => {
          setShowAddressModal(false);
          fetchAddresses();
        }} 
      />
    </div>
  );
};

export default CheckoutPage;
